<template>
  <div>
    <!-- Income Information -->
    <h1>Income Information</h1>

    <div class="row">
      <div class="col-12 headerUnderline">
        <img src="./assets/images/Underline.png">
      </div>
    </div>

    <div class="row">
      <FormGroup
        class="col-12"
        label="Income Type"
        :validator="$v.loan.incometype"
      >
        <select
          v-model="model.incometype"
          required
        >
          <option value="">
            - Select -
          </option>
          <option
            v-for="item in incomeTypes"
            :key="item.type"
            :value="item.type"
          >
            {{ item.label }}
          </option>
        </select>
      </FormGroup>
    </div>

    <div class="row">
      <FormGroup
        class="col-12"
        label="Payroll Payment Method"
        :validator="$v.loan.paytype"
      >
        <select
          v-model="model.paytype"
          required
        >
          <option value="">
            - Select -
          </option>
          <option
            v-for="item in paymentMethods"
            :key="item.type"
            :value="item.type"
          >
            {{ item.label }}
          </option>
        </select>
      </FormGroup>
    </div>

    <div class="row">
      <FormGroup
        class="col-12"
        label="Payment Frequency"
        :validator="$v.loan.payfrequency"
      >
        <select
          v-model="model.payfrequency"
          required
        >
          <option value="">
            - Select -
          </option>
          <option
            v-for="item in payFrequencies"
            :key="item.type"
            :value="item.type"
          >
            {{ item.label }}
          </option>
        </select>
      </FormGroup>
    </div>

    <div class="row">
      <FormGroup
        class="col-12"
        label="Date of Most Recent Payment from Employer"
        :validator="$v.loan.lastpaydate"
      >
        <DatePicker
          v-model.lazy="model.lastpaydate"
          placeholder="MM/DD/YYYY"
          required
        />
        <template #error>
          <i v-if="!$v.loan.lastpaydate.minValue">Must be a date in the past.</i>
        </template>
      </FormGroup>
    </div>

    <div class="row">
      <FormGroup
        class="col-12"
        label="Date of Next Payment Due from Employer"
        :validator="$v.loan.nextpaydate"
      >
        <DatePicker
          v-model.lazy="model.nextpaydate"
          placeholder="MM/DD/YYYY"
          required
        />
        <template #error>
          <i v-if="!$v.loan.nextpaydate.validNextPaydate">
            Must be a future date.
          </i>
        </template>
      </FormGroup>
    </div>

    <div class="row">
      <FormGroup
        class="col-12"
        label="Monthly Income"
        :validator="$v.loan.grossmonthly"
      >
        <TextInput
          v-model.number="model.grossmonthly"
          v-inputmask="{ type: 'money' }"
          required
          placeholder="$ 0.00"
        />
      </FormGroup>
    </div>

    <div class="row">
      <FormGroup
        class="col-12"
        label="Bank Name"
        :validator="$v.loan.bankname"
      >
        <TextInput v-model="model.bankname" />
      </FormGroup>
    </div>

    <div class="row">
      <FormGroup
        class="col-12"
        label="Bank Account Type"
        :validator="$v.loan.accounttype"
      >
        <input
          id="accounttype-checking"
          v-model="model.accounttype"
          type="radio"
          value="C"
          required
        >
        <label for="accounttype-checking">Checking</label>
        <input
          id="accounttype-savings"
          v-model="model.accounttype"
          type="radio"
          value="S"
          required
        >
        <label for="accounttype-savings">Savings</label>
      </FormGroup>
    </div>

    <div class="row">
      <FormGroup
        class="col-12"
        label="Bank Account Routing Number"
        :validator="$v.loan.routingnumber"
      >
        <TextInput
          v-model="model.routingnumber"
          v-inputmask="{ type: 'routingNumber' }"
          :readonly="status.isReturningCustomer"
          required
        />
      </FormGroup>
    </div>

    <div class="row">
      <FormGroup
        class="col-12"
        label="Bank Account Number"
        :validator="$v.loan.accountnumber"
      >
        <TextInput
          v-model="model.accountnumber"
          v-inputmask="{ type: 'accountNumber' }"
          :readonly="status.isReturningCustomer"
          required
        />
      </FormGroup>
    </div>

    <div
      v-if="leadProviderCampaignProductType === 'lineOfCredit'"
      class="row mt-l"
    >
      <FormGroup
        class="col-12"
        :validator="$v.loan.allow_credit_check"
      >
        <input
          id="allow_credit_check"
          v-model="model.allow_credit_check"
          type="checkbox"
        >

        <label for="allow_credit_check">
          <a
            @click.prevent="creditCheckModalOpen = true"
          >
            Consent to pull consumer credit report
          </a>
        </label>
      </FormGroup>
    </div>

    <div
      v-if="!model.consent_status"
      class="row mt-l"
    >
      <FormGroup class="col-12">
        <input
          id="sms_program_terms"
          v-model="model.consent_status"
          type="checkbox"
        >

        <label for="sms_program_terms">
          {{ settings.sms_terms }} View
          <a
            :href="settings.link_sms_terms_href"
            target="_new"
          >{{ settings.link_sms_terms_label }}</a>. View
          <a
            :href="settings.link_privacy_policy_href"
            target="_new"
          >{{ settings.link_privacy_policy_label }}</a>.
        </label>
      </FormGroup>
    </div>

    <div class="row mt-l">
      <div class="col-6 col-12-medium">
        <ul class="actions fit">
          <li>
            <button
              class="button primary fit icon fa-lock"
              @click="validateAndSubmit()"
            >
              SUBMIT
            </button>
          </li>
        </ul>
      </div>
    </div>
    <CreditCheckModal
      :open="creditCheckModalOpen"
      :bank-name="bankName"
      @close="creditCheckModalOpen = false"
    />
  </div>
</template>

<script>
import { mapState, mapActions } from "vuex";
import { DateTime } from "luxon";
import { incomeTypes, payFrequencies, paymentMethods } from "@/config/walkupFields";
import CreditCheckModal from "../CreditCheckModal.vue";
import validations from "../validations";

const LENDINGTREE_FROM_STRING = "lendingtree";

export default {
  name: "StepThree",
  components: {
    CreditCheckModal,
  },

  data() {
    return {
      incomeTypes,
      paymentMethods,
      payFrequencies,
      model: {},
      creditCheckModalOpen: false,
      stepFields: [
        "incometype",
        "paytype",
        "payfrequency",
        "lastpaydate",
        "nextpaydate",
        "grossmonthly",
        "bankname",
        "accounttype",
        "routingnumber",
        "accountnumber",
        "sms_program_terms"
      ]
    };
  },

  computed: {
    ...mapState("settings", ["settings", "apiUrl", "apiKey", "apiCampaignId"]),
    ...mapState("underwriting", ["status", "loan", "leadProviderCampaignProductType"]),
    bankName() {
      return this.settings?.company_long_name || "(Bank name is not set)";
    },
  },

  validations() {
    return {
      loan: validations,
    };
  },

  mounted() {
    this.model = this.loan;
  },

  methods: {
    ...mapActions("underwriting", ["setError", "setDeclined", "setProcessing", "updateLoan"]),
    validateAndSubmit() {
      let isValid = true;
      this.updateLoan({ ...this.model });
      this.stepFields.forEach((field) => {
        if (this.$v.loan[field]) {
          this.$v.loan[field].$touch();
          isValid = isValid && !this.$v.loan[field].$anyError;
        }
      });

      if (isValid) {
        this.sendForm();
      } else {
        this.$nextTick(() => {
          this.scroll(true);
        });
      }
    },

    handleFormPayload() {
      let payload = {};

      const {
        nextpaydate,
        payfrequency,
        grossmonthly,
        empphone,
        homephone,
        hiredate,
        // eslint-disable-next-line camelcase
        consent_status, allow_credit_check
      } = this.loan;

      payload = {
        ...this.loan,
        empphone: empphone.replace(/\s+/g, ""),
        homephone: homephone.replace(/\s+/g, ""),
        otheroffers: "false",
        affid: "8",
        subid: "22",
        testtype: 2,
        netmonthly: grossmonthly,
        secondpaydate: this.dateIncByFrequency(nextpaydate, payfrequency),
        // fields need to stay in the format they are returned from the api
        // eslint-disable-next-line camelcase
        consent_status: consent_status ? 1 : 0,
        allow_credit_check,
        api_key: this.apiKey,
        campaign: this.apiCampaignId,
        walkup_version: process.env.VUE_APP_VERSION,
      };

      const hireDate = this.handleHiredDate(hiredate);

      payload = {
        ...payload,
        ...hireDate,
        lastpaydate: this.handleLastPayDate(),
        return_customer: (this.status.isReturningCustomer) ? 1 : 0
      };

      if (this.isLendingTreeCustomer) {
        payload = {
          ...payload,
          user_from: LENDINGTREE_FROM_STRING,
          user_from_id: this.loan.user_from_id,
        };
      }

      // if campaign has data overide default
      if (this.campaignInfo) {
        payload = { ...payload, ...this.campaignInfo };
      }

      return payload;
    },

    dateIncByFrequency(nextpaydate, payfrequency) {
      const date = DateTime.fromISO(nextpaydate);

      if (payfrequency === "B") {
        return date.plus({ weeks: 2 }).toISODate();
      }
      if (payfrequency === "S") {
        return date.plus({ days: 15 }).toISODate();
      }
      if (payfrequency === "M") {
        return date.plus({ months: 1 }).toISODate();
      }

      return date.plus({ weeks: 1 }).toISODate();
    },

    handleHiredDate(hiredate) {
      // Handle hiredate
      if (hiredate) {
        const hireDate = DateTime.fromISO(hiredate);
        const employedDuration = DateTime.local()
          .diff(hireDate, ["months", "years"])
          .toObject();

        return {
          hiredate,
          empmonths: (this.loan.empmonths) ? this.loan.empmonths : Math.round(employedDuration.months),
          empyears: (this.loan.empyears) ? this.loan.empyears : Math.round(employedDuration.years),
        };
      }

      if (this.loan.empyears || this.loan.empmonths) {
        const actualMonth = new Date().getMonth() + 1;
        const dateNeeded = new Date();

        dateNeeded.setMonth(actualMonth - this.loan.empmonths || 0);
        dateNeeded.setFullYear(dateNeeded.getFullYear() - this.loan.empyears || 0);

        const hireDate = DateTime.fromJSDate(dateNeeded).toISODate();

        return {
          empmonths: this.loan.empmonths,
          empyears: this.loan.empyears,
          hiredate: hireDate,
        };
      }
      const hireDate = DateTime.now().toISODate();

      return {
        empmonths: 1,
        empyears: 0,
        hiredate: hireDate,
      };
    },

    handleLastPayDate() {
      // Add last pay date if not exist

      if (this.loan.lastpaydate) {
        return this.loan.lastpaydate;
      }

      const frequencies = [
        { type: "W", days: 7 },
        { type: "B", days: 14 },
        { type: "S", days: 15 },
        { type: "M", days: 30 },
      ];

      const frequency = frequencies.find((freq) => freq.type === this.loan.payfrequency);
      return DateTime.fromISO(this.loan.nextpaydate).minus({ days: frequency.days }).toISODate();
    },

    async sendForm() {
      this.setError(false);
      this.setDeclined(false);
      this.setProcessing(true);
      this.checkIsMilitary();
      this.checkIsNonLendingState();

      const payload = this.handleFormPayload();

      try {
        const { status, data } = await this.$axios.post(`${this.apiUrl}api`, payload);

        localStorage.removeItem("loanData");
        if (status === 200) {
          if (data.result === "lead_accepted") {
            this.pushGtmDataLayer({
              reference: data.reference,
              redirect: data.redirect,
            });

            window.location.assign(data.redirect);
          } else {
            this.setProcessing(false);
            this.setDeclined(true);
          }
        } else {
          this.setProcessing(false);
          this.setError(true);
        }
      } catch (e) {
        this.setProcessing(false);
        this.setError(true);
      }
    },

    pushGtmDataLayer(lead = null) {
      if (this.$gtm.enabled()) {
        window.dataLayer = window.dataLayer || [];

        let layer = {
          email: this.loan?.email || "",
          cust_id: this.loan?.customer_guid || "",
        };

        if (lead) {
          layer = {
            ...layer,
            lead_id: lead.reference,
            lead_guid: lead.redirect.substring(
              lead.redirect.lastIndexOf("/") + 1
            ),
          };
        }

        window.dataLayer.push(layer);
      }
    },

    /**
     * Check is non lending states and redirect if needed.
     */
    checkIsNonLendingState() {
      // if company has non-lending states set in config, and a non-lending-states href,
      // send them to their href upon selecting one.
      const nonLendingStates = this.settings.non_lending_state;
      if (nonLendingStates.length > 0) {
        if (
          nonLendingStates.includes(this.loan.state)
          && this.settings.link_non_lending_states_href
        ) {
          window.location.assign(this.settings.link_non_lending_states_href);
        }
      }
    },

    /**
     * Check if applicant is military.
     */
    checkIsMilitary() {
      // if company has a mla href, and they select ismilitary
      if (this.loan.ismilitary === "true" && this.settings.link_mla_href) {
        window.location.assign(this.settings.link_mla_href);
      }
    },

    /**
     * Scroll to top or if error exist,
     * scroll to error.
     * @param toError
     */
    scroll(toError = false) {
      let top = 0;

      if (toError) {
        const domRect = document
          .querySelector(".with-errors")
          .getBoundingClientRect();
        top = domRect.top + document.documentElement.scrollTop - 230;
      }

      window.scrollTo({
        top,
        behavior: "smooth",
      });
    },
  },
};
</script>
